import React from 'react';
import { HandHeart } from 'lucide-react';

const money = (s, v) => `${s}${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

// choice: 'suggested' | 'min' | 'more' | 'custom' | 'zero'
export const donationAmount = (program, people, choice, custom) => {
  const sug = (program.suggested_donation || 0) * people;
  if (choice === 'min') return (program.donation_min || 0) * people;
  if (choice === 'more') return Math.round(sug * 1.5);
  if (choice === 'custom') return Math.max(0, parseFloat(custom) || 0);
  if (choice === 'zero') return 0;
  return sug;
};

export const DonationPicker = ({ program, people, s, choice, setChoice, custom, setCustom }) => {
  const sug = (program.suggested_donation || 0) * people;
  const min = (program.donation_min || 0) * people;
  const value = (program.donation_value_received || 0) * people;
  const opts = [['suggested', `Suggested ${money(s, sug)}`]];
  if (min > 0 && min < sug) opts.push(['min', `Minimum ${money(s, min)}`]);
  opts.push(['more', `Give more ${money(s, Math.round(sug * 1.5))}`]);
  const amt = donationAmount(program, people, choice, custom);
  const below = choice === 'custom' && min > 0 && amt < min;
  return (
    <div className="pt-2 border-t border-rose/40" data-testid="suggested-donation-block">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-mahogany/70 mb-1.5"><HandHeart className="w-3.5 h-3.5" /> Your donation</div>
      <p className="text-xs text-mahogany/70 mb-2">This program is offered on a suggested-donation basis{people > 1 ? ` (${money(s, program.suggested_donation)} per person)` : ''}.{min > 0 ? ` A minimum gift of ${money(s, min)} helps cover costs.` : ' Give what feels right.'}</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {opts.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setChoice(k)} data-testid={`donation-choice-${k}`}
            className={`py-2 px-2 rounded-xl text-sm border transition-colors ${choice === k ? 'bg-burgundy text-cream border-burgundy' : 'bg-white/70 text-burgundy border-rose/50 hover:bg-white'}`}>{l}</button>
        ))}
      </div>
      <div className="mt-2 flex items-center gap-2">
        <span className="text-sm text-mahogany/70">{s}</span>
        <input type="number" min="0" step="1" value={choice === 'custom' ? custom : ''} onFocus={() => setChoice('custom')} onChange={(e) => { setChoice('custom'); setCustom(e.target.value); }}
          placeholder="Other amount" data-testid="donation-choice-custom" className="flex-1 bg-white rounded-xl border border-rose/50 px-3 py-2 text-sm text-burgundy focus:outline-none focus:border-mahogany" />
      </div>
      {below && <p className="text-[11px] text-red-600 mt-1" data-testid="donation-below-min">The minimum gift is {money(s, min)}.</p>}
      {program.donation_allow_zero && !min && (
        <button type="button" onClick={() => setChoice('zero')} data-testid="donation-choice-zero" className={`mt-2 text-xs underline ${choice === 'zero' ? 'text-burgundy font-medium' : 'text-mahogany/60 hover:text-burgundy'}`}>I can't give right now — register without a gift</button>
      )}
      <p className="text-[11px] text-mahogany/60 mt-2">Meditative Mindfulness Inc is a 501(c)(3) (EIN 92-1474053). You'll get a tax receipt by email{value > 0 ? `; the estimated value of what you receive (${money(s, value)}) isn't deductible` : ''}.</p>
    </div>
  );
};

export default DonationPicker;
